import { NumericFormat } from 'react-number-format'
import {
  Accordion,
  AccordionItem,
  Button,
  LoadingSpinner,
  TextInput,
} from '@resurs/styx-react'
import moment from 'moment'
import { getText } from '../../../internal/Text'
import {
  breakLines,
  haveFlexProducts,
  renderFlexibleInvoiceOption,
  renderFooter,
  renderOption,
  shouldShowSecondReservationText,
} from './helpers'
import { getLocale } from '../PriceInfo'
import logo from '../../../assets/logo.svg'
import header from '../../../assets/header.svg'
import styles from './priceinfo.module.scss'
import CreditWarning from './CreditWarning'

const isStandardOrStandmark = option => {
  const value = option.visibilityPaymentPlan?.toUpperCase()
  return value === 'STANDARD' || value === 'STANDMARK'
}

const sortStandardLast = (a, b) => {
  const aIsLast = isStandardOrStandmark(a)
  const bIsLast = isStandardOrStandmark(b)
  if (aIsLast === bIsLast) return 0
  return aIsLast ? 1 : -1
}

const PriceInfoUI = ({
  amount,
  priceInfo,
  isLoading,
  onAmountChange,
  onAmountSubmit,
}) => (
  <>
    <div className={styles.wrapper}>
      <div className={styles.headerContainer}>
        <img src={logo} className="App-logo" alt="logo" />
        <img src={header} className="App-header" alt="header" />
      </div>
      <div className={styles.mainContainer}>
        {!isLoading ? (
          priceInfo ? (
            <>
              <div className={styles.calculateContainer}>
                <h1>{getText('priceInfo.header.title')}</h1>
                <form onSubmit={onAmountSubmit}>
                  <div className={styles.inputGroup}>
                    <div className={styles.inputGroupItem}>
                      <TextInput
                        id="creditAmount"
                        name="amount"
                        type="number"
                        value={amount || ''}
                        label={getText('priceInfo.calculate.label')}
                        placeholder={getText('priceInfo.calculate.action')}
                        onChange={(id, value) => onAmountChange(value)}
                      />
                    </div>
                    <div className={styles.inputGroupItem}>
                      <Button
                        id="creditAmountSubmitButton"
                        type="primary"
                        disabled={!amount}
                        onClick={onAmountSubmit}
                      >
                        {getText('priceInfo.calculate.action')}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
              {priceInfo.isFlexibleInvoice ? (
                <>
                  <div className={styles.mainContentHeader}>
                    <h3>{getText('priceInfo.general.invoice')}</h3>
                  </div>
                  <div className={styles.optionsContent}>
                    {renderFlexibleInvoiceOption(priceInfo.flexibleInvoice)}
                  </div>
                  {priceInfo.flexibleInvoice.termsLink && (
                    <div className={styles.documentContainer}>
                      <h2>{getText('priceInfo.documents.title')}</h2>
                      <Accordion>
                        <AccordionItem
                          id="termsLink"
                          label={getText('priceInfo.documents.generalTerms')}
                          onClick={() =>
                            window.open(
                              priceInfo.flexibleInvoice.termsLink,
                              '_blank',
                              'noreferrer',
                            )
                          }
                        />
                      </Accordion>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className={styles.mainContentHeader}>
                    <h3>{priceInfo.name}</h3>
                    <span>{getText('priceInfo.header.subtitle')}: </span>
                    <span className={styles.numericFormat}>
                      <NumericFormat
                        value={priceInfo.amount}
                        displayType="text"
                        thousandSeparator=" "
                        decimalSeparator=","
                        decimalScale="2"
                        fixedDecimalScale
                        renderText={val => getText('default.currency', val)}
                      />
                    </span>
                  </div>
                  <div className={styles.optionsContent}>
                    {priceInfo.options
                      .filter(
                        option =>
                          option.type.toUpperCase() === 'BASE' ||
                          option.type.toUpperCase() === 'LOWEST_PAYMENT',
                      )
                      .sort(sortStandardLast)
                      .map((option, idx) =>
                        renderOption(priceInfo, option, idx),
                      )}
                  </div>
                  {haveFlexProducts(priceInfo.options) && (
                    <>
                      <div className={styles.flexContentHeader}>
                        <h3>{getText('priceInfo.flex.title')}</h3>
                        <span>{getText('priceInfo.flex.description')}</span>
                      </div>
                      <div className={styles.optionsContent}>
                        {priceInfo.options
                          .filter(
                            option => option.type.toUpperCase() === 'FLEX',
                          )
                          .sort(sortStandardLast)
                          .map((option, idx) =>
                            renderOption(priceInfo, option, idx),
                          )}
                      </div>
                    </>
                  )}
                  <div className={styles.reservationContainer}>
                    <span>
                      {getText('priceInfo.reservations.updatedAt')}{' '}
                      <span>{moment().format('dddd LL')}</span>.{' '}
                    </span>
                    <ul>
                      <li>
                        {breakLines(getText('priceInfo.reservations.first'))}
                      </li>
                      {shouldShowSecondReservationText(priceInfo) ? (
                        <>
                          <li>{getText('priceInfo.reservations.second')}</li>
                        </>
                      ) : null}
                    </ul>
                  </div>
                  <div className={styles.documentContainer}>
                    <h2>{getText('priceInfo.documents.title')}</h2>
                    <Accordion>
                      {priceInfo.sekkiLinks.map(({ uri, language }) => (
                        <AccordionItem
                          key={uri}
                          id={`sekkiLink-${uri}`}
                          label={addExtensionIfFinnishLinkWithSwedishText(
                            getText('priceInfo.documents.sekki'),
                            language,
                          )}
                          onClick={() =>
                            window.open(uri, '_blank', 'noreferrer')
                          }
                        />
                      ))}
                      {priceInfo.generalTermsLinks.map(({ uri, language }) => (
                        <AccordionItem
                          key={uri}
                          id={`generalTermsLink-${uri}`}
                          label={addExtensionIfFinnishLinkWithSwedishText(
                            getText('priceInfo.documents.generalTerms'),
                            language,
                          )}
                          onClick={() =>
                            window.open(uri, '_blank', 'noreferrer')
                          }
                        />
                      ))}
                    </Accordion>
                    {getLocale().country === 'SE' && <CreditWarning />}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className={styles.errorContainer}>
              {getText('priceInfo.general.errorMessage')}
            </div>
          )
        ) : (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
          </div>
        )}
      </div>
      {renderFooter()}
    </div>
  </>
)

const addExtensionIfFinnishLinkWithSwedishText = (originalText, language) => {
  const locale = getLocale()

  if (locale.country === 'FI' && language.toString() === 'Swedish') {
    return `${originalText} (sv)`
  }

  return originalText
}

export default PriceInfoUI
